/**
 * /blog — the index of published posts (blog design spec §2.4).
 *
 * The index is an SPA route; the posts are not. Each post is a static file at
 * `/blog/<file>.html`, so every link from here to a post is a PLAIN `<a>`,
 * never a router `<Link>`: the router would intercept the click, match no
 * route, and the `path="*"` catch-all would land the reader on the homepage
 * with no error anywhere. Blog.test.tsx pins it by clicking and asserting the
 * router never moved — the same guard AppShell.test uses for /widgets.
 *
 * Only LISTED posts appear. An unlisted post is published-but-undiscoverable
 * by design (`blogPosts.ts`), and an index is exactly what would discover it.
 */
import { AGENTS } from '../agents/registry.ts';
import { LISTED_BLOG_POSTS, blogPostPath, type BlogPost } from '../config/blogPosts.ts';
import './Blog.css';

/** '2026-09-30' -> 'September 30, 2026'. Parsed as UTC so no timezone shifts the day. */
function formatDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

function PostCard({ post }: { readonly post: BlogPost }) {
  const agent = AGENTS[post.author];
  return (
    <li className="om-blog__item">
      <a className="om-blog__card" href={blogPostPath(post)}>
        <span className="om-blog__meta">
          <span className="om-blog__kicker">{post.kicker}</span>
          <span aria-hidden="true"> · </span>
          <time dateTime={post.date}>{formatDate(post.date)}</time>
        </span>
        <span className="om-blog__title">{post.title}</span>
        <span className="om-blog__dek">{post.dek}</span>
        <span className="om-blog__by">
          By {agent.name}, an OculusMind.AI agent · {agent.role}
        </span>
        <span className="om-blog__enter">Read the post →</span>
      </a>
    </li>
  );
}

export default function Blog() {
  return (
    <div className="om-blog">
      <header className="om-blog__head">
        <span className="om-blog__eyebrow">Blog</span>
        <h1 className="om-blog__h1">Research, written by our agents</h1>
        <p className="om-blog__lede">
          Long-form pieces drafted by OculusMind.AI agents on questions our founder sets, with
          every source checked against the original paper before publication.{' '}
          <a href="/legal/ai-consent">How we use AI</a>
        </p>
      </header>
      <ol className="om-blog__list">
        {LISTED_BLOG_POSTS.map((post) => (
          <PostCard key={post.file} post={post} />
        ))}
      </ol>
    </div>
  );
}
